import React from 'react';
import {Avatar, Tooltip} from 'antd';
import ExpenseRowActionCell from '../ExpenseRowActionCell';
import IconWrapper from '../../../IconWrapper';
import {StarsIcon, TeamsOutlinedIcon} from '../../../../icons';
import {
  accountingHelpers,
  dateHelpers,
  expensesHelpers,
  reviewHelpers,
  tableHelpers,
} from '../../../../utils/helpers';
import {
  StyledExpensesTabActionsSpace,
  StyledExpensesTabActionsGhostSpace,
} from './StyledExpensesTab';
import {colorsStyles} from '../../../../utils/styles';

const dateColumnProps = accountingHelpers.getDateColumnProps();
const summaraizeKeyName = 'isMonthGrouping';
const columnProps = { width: '20%' };

export function getExpensesTabColumns({ t, isAdmin, employeeEmail, onApprove, onReject }) {
  const getAvatar = tableHelpers.getUserAvatar;

  const getDate = (value, record) => dateHelpers.getDateFromISO(value || record?.created_at, record?.[summaraizeKeyName] ? 'MMM YYYY' : 'DD MMM');

  const handleAction = ({ action, e, expense }) => {
    const isEmployeeInApprovalList = reviewHelpers.isEmployeeInApprovalList(expense, employeeEmail);
    const isAlreadyApproved = reviewHelpers.isAlreadyApprovedByEmployee(expense, employeeEmail);
    const override = isEmployeeInApprovalList && !isAlreadyApproved ? false : isAdmin;
    action?.({ e, expense, data: { admin_override: override } });
  };

  const getActions = (expense) => {
    if (expense?.[summaraizeKeyName]) return;
    const isExpenseInReview = expensesHelpers.isExpenseInReview(expense);
    const isExpenseDraft = expensesHelpers.isExpenseDraft(expense);
    const isEmployeeInApprovalList = reviewHelpers.isEmployeeInApprovalList(expense, employeeEmail);
    const isAlreadyApproved = reviewHelpers.isAlreadyApprovedByEmployee(expense, employeeEmail);
    const isAnyAdminApproval = reviewHelpers.isAnyAdminApproval(expense);

    const canTakeAction = isExpenseDraft ? isAdmin : isExpenseInReview && (
      (isAdmin && isAnyAdminApproval) ||
      ((isAdmin || isEmployeeInApprovalList) && !isAlreadyApproved)
    );

    return (
      <StyledExpensesTabActionsSpace size='middle' align='center'>
        <StyledExpensesTabActionsGhostSpace size='middle' align='center'>
          {expensesHelpers.getExpenseReviewInfo({expense, translation: t})}

          <ExpenseRowActionCell
            canTakeAction={canTakeAction}
            onApprove={onApprove}
            onReject={onReject}
            expense={expense}
            handleAction={handleAction}
          />

        </StyledExpensesTabActionsGhostSpace>

      </StyledExpensesTabActionsSpace>
    );
  };

  const getIconWithTooltip = ({icon, title, wrapperProps} = {}) => (
    <Tooltip title={title}>
      <IconWrapper size={24} colorMode='stroke' className='d-flex' {...wrapperProps}>{icon}</IconWrapper>
    </Tooltip>
  );

  return [
    {
      ...dateColumnProps,
      ...columnProps,
      dataIndex: 'date',
      render: getDate,
      title: `${t('creation')} ${t('date')}`,
    },
    {
      ...dateColumnProps,
      ...columnProps,
      dataIndex: 'expense_date',
      render: (value, record) => {
        if (record?.[summaraizeKeyName]) return;
        return expensesHelpers.getFormattedExpenseDate(record, 'DD MMM');
      },
      sorter: false,
      title: `${t('expense')} ${t('date')}`,
    },
    {
      dataIndex: 'payment_type',
      title: `${t('payment')} ${t('method')}`,
      render: (_, record) => {
        if (record?.[summaraizeKeyName]) return;
        return expensesHelpers.getExpensePaymentMethod(record, t);
      }
    },
    {
      ...columnProps,
      dataIndex: 'category_tags',
      render: (_, record) => {
        if (record?.[summaraizeKeyName]) return;
        const categoryTags = record?.category_tags || [];
        return categoryTags[0] || '';
      },
      title: t('category'),
    },
    {
      ...columnProps,
      dataIndex: 'reported_by',
      render: (user, record) => {
        if (record?.[summaraizeKeyName]) return;
        return getAvatar({ user });
      },
      title: t('user'),
    },
    {
      ...columnProps,
      dataIndex: 'approval',
      render: (_, record) => {
        if (record?.[summaraizeKeyName]) return;
        const approver = reviewHelpers.getApprovals(record);
        const isEmptyApprover = approver.length === 0;

        if (isEmptyApprover && reviewHelpers.isAnyAdminApproval(record)) {
          return getIconWithTooltip({icon: <TeamsOutlinedIcon />, title: `${t('any')} ${t('admin')}`});
        }

        if (isEmptyApprover && reviewHelpers.isAutoApproval(record)) {
          return getIconWithTooltip({
            icon: <StarsIcon />,
            title: t('automaticallyApproved'),
            wrapperProps: {
              color: colorsStyles.additional_purple,
              size: 18,
              style: {
                backgroundColor: colorsStyles.additional_purple_light,
                borderRadius: '50%',
                width: 24,
                height: 24,
              }
            }
          });
        }

        const adminOverride = reviewHelpers.getAdminOverrideApproval(record);
        if (adminOverride) {
          return getAvatar({ user: adminOverride.approver });
        }

        return (
          <Avatar.Group>
            {approver.map(({ approver }, key) => getAvatar({ user: approver, key }))}
          </Avatar.Group>
        );
      },
      title: t('approver'),
    },
    {
      ...columnProps,
      align: 'right',
      dataIndex: 'price',
      render: (_, record) => (
        <div className='amount'>
          {expensesHelpers.getExpensePrice(record)}
        </div>
      ),
      title: t('amount'),
    },
    {
      align: 'right',
      dataIndex: 'match',
      render: (_, record) => getActions(record),
      title: ' '
    }
  ];
}
